import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useConsistencyCheck } from '../../api/queries';
import { ProblemAlert } from '../../components/ProblemAlert';

/**
 * Konzisztencia-ellenőrzés — a spec 22. képernyőjének („hibás vagy
 * ellenőrzést igénylő rekordok") technikai megfelelője.
 *
 * Az ÜRES lista a jó eredmény, és ez nem semmitmondó: ez a folyamatos
 * bizonyíték arra, hogy az „egy mappának pontosan egy aktív lokációja van"
 * invariáns (spec 1. alapelv) tényleg él. Ezért a nulla találatot
 * hangsúlyosan, zölden mutatjuk — nem üres képernyővel, amiből nem derül
 * ki, hogy lefutott-e egyáltalán.
 *
 * A `sampleIds` szándékosan csak néhány azonosító (a szerver így adja):
 * kivizsgálás indítására szolgál, nem a teljes lista megjelenítésére.
 */
export function ConsistencyPage() {
  const check = useConsistencyCheck();

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
        <Typography variant="h5" component="h2" sx={{ flexGrow: 1 }}>
          Konzisztencia-ellenőrzés
        </Typography>
        <Button
          startIcon={<RefreshIcon />}
          onClick={() => void check.refetch()}
          disabled={check.isFetching}
        >
          Újrafuttatás
        </Button>
      </Stack>

      <Typography variant="body2" color="text.secondary">
        Az adatbázis-invariánsok ellenőrzése. Az itt megjelenő tételek olyan állapotok, amiket a
        rendszer nem tud önmagában helyrehozni — mindegyik emberi kivizsgálást igényel.
      </Typography>

      {check.isError && <ProblemAlert error={check.error} />}

      {check.isPending ? (
        <Stack sx={{ py: 6, alignItems: 'center' }}>
          <CircularProgress />
        </Stack>
      ) : check.data ? (
        check.data.length === 0 ? (
          <Alert severity="success" icon={<CheckCircleOutlinedIcon />}>
            <AlertTitle>Nincs eltérés</AlertTitle>
            Minden ellenőrzés hibátlan. Ez azt jelenti, hogy egyetlen mappának sincs hiányzó vagy
            duplikált aktív lokációja, a raklapszintek konzisztensek, és a címke-sorszámok nem
            csúsztak el.
          </Alert>
        ) : (
          <Stack spacing={2}>
            <Alert severity="warning">
              <AlertTitle>{check.data.length} ellenőrzés talált eltérést</AlertTitle>
              Ezek nem automatikusan javuló hibák — mindegyiket meg kell vizsgálni.
            </Alert>

            {check.data.map((violation) => (
              <Paper key={violation.check} variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={1}>
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                    <Chip size="small" color="warning" label={`${violation.count} db`} />
                    <Typography variant="subtitle2" sx={{ fontFamily: 'monospace' }}>
                      {violation.check}
                    </Typography>
                  </Stack>

                  <Typography variant="body2">{violation.description}</Typography>

                  {violation.sampleIds.length > 0 && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Példák kivizsgáláshoz:
                      </Typography>
                      <Box
                        component="ul"
                        sx={{ m: 0, pl: 3, fontFamily: 'monospace', fontSize: '0.8rem' }}
                      >
                        {violation.sampleIds.map((id) => (
                          <li key={id}>{id}</li>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Stack>
              </Paper>
            ))}
          </Stack>
        )
      ) : null}
    </Stack>
  );
}
